import { useTranslation } from "react-i18next";

import { Spinner } from "../../../../../components/ui/spinner";
import type { CategoryRoom } from "../../../../../api/categoryRooms/categoryRoomsApiSlice";
import ApartmentListCard from "../ApartmentRental/ApartmentListCard";
import ListLoadError from "../ListLoadError";

interface CategoryRoomPickerProps {
  rooms: CategoryRoom[] | undefined;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  onPick: (room: CategoryRoom) => void;
}

/**
 * The first screen of an object-first category (#3035, M1): its rooms, before
 * any date. Picking one leads to the time step for that room — see
 * `useObjectFirstEntry`.
 *
 * Loading, failure and an empty list are three different screens (#1970): a
 * failed load must not read as "this category has no rooms", and an empty
 * answer is said in words rather than as a blank page.
 *
 * The card is the same one the dated search lists, without its price line: a
 * price depends on dates this screen does not have yet.
 */
const CategoryRoomPicker = ({
  rooms,
  loading,
  failed,
  onRetry,
  onPick,
}: CategoryRoomPickerProps) => {
  const { t } = useTranslation();

  if (failed) {
    return (
      <ListLoadError
        messageKey="book_location.object_first.load_failed"
        action={{ labelKey: "book_location.object_first.retry", onClick: onRetry }}
      />
    );
  }
  if (loading || !rooms) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner size="lg" ariaLabel={t("common.loading")} />
      </div>
    );
  }
  if (rooms.length === 0) {
    return (
      <p className="px-6 py-10 text-center text-sm text-foreground/70">
        {t("book_location.object_first.no_rooms")}
      </p>
    );
  }

  return (
    <div className="mx-4">
      <p className="mb-4 text-sm text-foreground/70">
        {t("book_location.object_first.intro")}
      </p>
      {rooms.map((room) => (
        <ApartmentListCard
          key={room.groupId}
          data={{
            ...room,
            featuredImage: room.featuredImage ?? "",
            langNames: room.langNames ?? {},
          }}
          onSelectApartment={() => onPick(room)}
          hidePrice
        />
      ))}
    </div>
  );
};

export default CategoryRoomPicker;
